import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { SearchIcon } from 'lucide-react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { statisticsService } from '../services/statisticsService';
import type { StatisticsFilters } from '../services/statisticsService';
import type { 
  OverviewStats 
} from '../../generated-src/client/statistics/api';
import type { 
  RecruiterDetailItem 
} from '../../generated-src/client/statistics/api';
import type { 
  InterviewDetailItem 
} from '../../generated-src/client/statistics/api';
import { useBrandingColors, getButtonInlineStyles, getButtonHoverStyles } from '../hooks/useBrandingColors';
import { useVacancyStore } from '../store/vacancyStore';
import { DownloadPdfButton } from './ui/download-pdf-button';
import { apiService } from '../services/apiService';
import {
  StatisticsPeriod,
  StatisticsInterviewStatusEnum
} from '../../generated-src/client/statistics/api';
import { 
  StatisticsPeriod as StatisticsPeriodEnum,
  StatisticsInterviewStatusEnum as StatusEnum
} from '../../generated-src/client/statistics/api';
// Removed search/filters UI components and related services
import { ReactComponent as LiveBadgeIcon } from '../assets/icons/interview-live.svg';
import { ReactComponent as AiBadgeIcon } from '../assets/icons/interview-ai.svg';
import { Pagination } from './vacancies/components/pagination';
import { CandidateAnswersPanel } from './vacancies/CandidateAnswersPanel';

type NavigationTab = 'vacancies' | 'statistics' | 'team';
type StatisticsViewMode = 'table' | 'candidate-answers';

interface StatisticsPageCompleteProps {
  onTabChange?: (tab: NavigationTab) => void;
  onUserProfileClick?: () => void;
}

// Используем тип из API вместо локального интерфейса
type CandidateData = InterviewDetailItem;

const formatCandidateRating = (rating: number | null | undefined) => {
  if (rating === null || rating === undefined || rating === 0) return '—';
  const fixed = rating.toFixed(1);
  return fixed.endsWith('.0') ? fixed.slice(0, -2) : fixed;
};

// Единые фиксированные ширины колонок таблицы (px) для предсказуемой вёрстки
const TABLE_COL_WIDTHS = {
  name: 240,
  contacts: 160,
  meetingStatus: 160,
  type: 100,
  rating: 90,
  status: 130,
  date: 110
} as const;

const CandidateRow = memo(({ candidate, index, totalLength, getStatusBadge, getLiveStatusBadge, getLiveDecisionBadge, typeFilter, onLiveMeetingStatusChange, onCandidateClick }: {
  candidate: CandidateData;
  index: number;
  totalLength: number;
  getStatusBadge: (status: string) => JSX.Element;
  getLiveStatusBadge: (status: 'scheduled' | 'successful') => JSX.Element | null;
  getLiveDecisionBadge: (status: 'approved' | 'declined') => JSX.Element | null;
  typeFilter: 'all' | 'ai' | 'live';
  onLiveMeetingStatusChange?: (id: number, status: 'scheduled' | 'successful') => void | Promise<void>;
  onCandidateClick: (candidate: CandidateData) => void;
}) => {
  const isAI = candidate.interviewType !== 'live';
  const isLive = candidate.interviewType === 'live';
  const status = (candidate as any).status as string | undefined;

  
  const fioWidth = typeFilter === 'live' ? '1 1 330px' : '1 1 240px'; 
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const menuButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const [menuCoords, setMenuCoords] = React.useState<{ top: number; left: number; } | null>(null);

  const openMenu = () => {
    if (!menuButtonRef.current) {
      setIsMenuOpen((v) => !v);
      return;
    }
    const rect = menuButtonRef.current.getBoundingClientRect();
    const menuWidth = 160;
    const padding = 8;
    const left = Math.min(Math.max(rect.left, padding), window.innerWidth - menuWidth - padding);
    const top = rect.bottom + 6;
    setMenuCoords({ top, left });
    setIsMenuOpen(true);
  };

  useEffect(() => {
    if (!isMenuOpen) return;
    const handle = () => setIsMenuOpen(false);
    window.addEventListener('scroll', handle, true);
    window.addEventListener('resize', handle);
    return () => {
      window.removeEventListener('scroll', handle, true);
      window.removeEventListener('resize', handle);
    };
  }, [isMenuOpen]);

  return (
    <div key={candidate.id} className="flex flex-col gap-[1.75px]" data-row="candidate">
      <div className="bg-white rounded-xl min-h-16 flex items-start overflow-hidden w-full">
        {/* ФИО */}
        <div className="px-3.5 py-[10.5px] flex items-start overflow-hidden" style={{ flex: fioWidth, minWidth: '200px' }}>
          <div className="flex flex-col gap-[3.5px] w-full min-w-0">
            {isLive ? (
              <span
                className="font-medium text-[14px] text-[#0a0d14] whitespace-normal break-words text-left"
                style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                {candidate.candidateName || '—'}
              </span>
            ) : (
              <button
                type="button"
                className="font-medium text-[14px] text-[#0a0d14] whitespace-normal break-words text-left cursor-pointer hover:underline"
                style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                onClick={() => onCandidateClick(candidate)}
              >
                {candidate.candidateName || '—'}
              </button>
            )}
            <span className="font-normal text-[12px] text-[#525866] leading-[18px] truncate whitespace-nowrap">
              {isLive && !candidate.candidateEmail ? '' : (candidate.candidateEmail || '—')}
            </span>
          </div>
        </div>

        {/* Контакты */}
        <div className="px-3.5 py-[10.5px] flex items-start overflow-hidden" style={{ flex: '0 0 150px', minWidth: '120px' }}>
          <span className="font-normal text-[14px] text-[#0a0d14] whitespace-nowrap">{(candidate as any).candidatePhone || '-'}</span>
        </div>

        {/* Тип - только для "all" */}
        {typeFilter === 'all' && (
          <div className="px-3.5 py-[10.5px] flex items-center overflow-hidden" style={{ flex: '0 0 100px', minWidth: '80px' }}>
            {(() => {
              const alt = isAI ? 'AI interview' : 'Live interview';
              const Icon = isAI ? AiBadgeIcon : LiveBadgeIcon;
              return <Icon className="h-4 w-auto" title={alt} aria-label={alt} />;
            })()}
          </div>
        )}

        {/* Оценка - для "all" и "ai" */}
        {(typeFilter === 'all' || typeFilter === 'ai') && (
          <div className="px-3.5 py-[10.5px] flex items-center justify-center" style={{ flex: '0 0 90px', minWidth: '70px' }}>
            <span className="font-medium text-[14px] text-[#0a0d14] tabular-nums">
              {typeof candidate.rating === 'number' ? formatCandidateRating(candidate.rating) : '—'}
            </span>
          </div>
        )}

        {/* Дата */}
        <div className="px-3.5 py-[10.5px] flex items-center justify-end" style={{ flex: '0 0 110px', minWidth: '90px' }}>
          <span className="font-normal text-[14px] text-[#0a0d14] tabular-nums">
            {candidate.date ? statisticsService.formatDateForTable(candidate.date) : '—'}
          </span>
        </div>

        {/* Статус */}
        <div className="px-3.5 py-[10.5px] flex items-center overflow-hidden" style={{ flex: '0 0 130px', minWidth: '110px' }}>
          {(() => {
            if (!isAI) {
              // Для live показываем только итог: Одобрено (successful) или Отменено (failed)
              if (status === 'successful') {
                return getLiveDecisionBadge('approved');
              }
              if (status === 'failed') {
                return getLiveDecisionBadge('declined');
              }
              return <span className="font-normal text-[14px] text-[#0a0d14]">—</span>;
            }
            // Для AI оставляем как есть
            return getStatusBadge(candidate.status);
          })()}
        </div>

        {typeFilter !== 'ai' && (
          <div className="px-3.5 py-[10.5px] flex items-center overflow-hidden" style={{ flex: typeFilter === 'live' ? '0 0 180px' : '1 1 150px', minWidth: '130px' }}>
            {(() => {
              if (!isAI) {
                const normalized = status === 'scheduled' || status === 'successful' ? (status as 'scheduled' | 'successful') : undefined;
                if (!normalized) {
                  return <span className="font-normal text-[14px] text-[#0a0d14]">{'—'}</span>;
                }
                if (!onLiveMeetingStatusChange) {
                  return getLiveStatusBadge(normalized);
                }
                return (
                  <div className="relative">
                  <button
                    type="button"
                    className="inline-flex items-center"
                    onClick={openMenu}
                    ref={menuButtonRef}
                  >
                    {getLiveStatusBadge(normalized)}
                  </button>
                    {isMenuOpen && menuCoords && createPortal(
                      <div className="fixed inset-0 z-[9999]" onClick={() => setIsMenuOpen(false)}>
                        <div
                          className="absolute w-[160px] bg-white border border-[#e2e4e9] rounded-lg shadow-sm"
                          style={{ top: menuCoords.top, left: menuCoords.left }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {(['scheduled', 'successful'] as const).map((opt) => (
                            <button
                              key={opt}
                              type="button"
                              className={`w-full text-left px-3 py-2 text-[13px] ${opt === normalized ? 'bg-[#f6f8fa] text-[#0a0d14]' : 'text-[#0a0d14] hover:bg-[#f6f8fa]'}`}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={async () => {
                                setIsMenuOpen(false);
                                if (opt !== normalized) {
                                  await onLiveMeetingStatusChange(candidate.id, opt);
                                }
                              }}
                            >
                              {opt === 'scheduled' ? 'Назначено' : 'Пройдено'}
                            </button>
                          ))}
                        </div>
                      </div>,
                      document.body
                    )}
                  </div>
                );
              }
              return <span className="font-normal text-[14px] text-[#0a0d14]">{'—'}</span>;
            })()}
          </div>
        )}
      </div>

      {/* Разделитель */}
      {index < totalLength - 1 && (
        <div className="h-px bg-[#e2e4e9] w-full"></div>
      )}
    </div>
  );
});

// Мемоизированный компонент для строки таблицы рекрутеров
const RecruiterRow = memo(({ recruiter, index, totalLength }: {
  recruiter: RecruiterDetailItem;
  index: number;
  totalLength: number;
}) => (
  <div
    key={recruiter.id}
    className={`bg-white rounded-xl min-h-16 flex ${
      index < totalLength - 1 ? 'border-b border-[#e2e4e9]' : ''
    }`}
  >
    <div className="px-3.5 py-[10.5px] w-[528px] flex items-center">
      <span className="font-medium text-[14px] text-[#0a0d14]">{recruiter.name}</span>
    </div>
    <div className="px-3.5 py-[10.5px] w-[246.39px] flex items-center">
      <div className="bg-[#f6f8fa] px-[10.5px] py-[5.25px] rounded-[10px]">
        <span className="font-medium text-[14px] text-[#0a0d14]">{recruiter.volume}</span>
      </div>
    </div>
    <div className="px-3.5 py-[10.5px] w-[246.39px] flex items-center">
      <span className="font-normal text-[14px] text-[#0a0d14]">{recruiter.completed}</span>
    </div>
    <div className="px-3.5 py-[10.5px] w-[246.39px] flex items-center">
      <div className={`px-[10.5px] py-[5.25px] rounded-[10px] ${
        recruiter.successRate >= 80 ? 'bg-[#f0f9f4]' : 
        recruiter.successRate >= 50 ? 'bg-yellow-50' : 'bg-red-50'
      }`}>
        <span className={`font-medium text-[14px] ${
          recruiter.successRate >= 80 ? 'text-[#38c793]' : 
          recruiter.successRate >= 50 ? 'text-yellow-600' : 'text-[#3b82f6]'
        }`}>
          {recruiter.successRate.toFixed(0)}%
        </span>
      </div>
    </div>
    <div className="px-3.5 py-[10.5px] w-[246.39px] flex items-center">
      <span className="font-medium text-[14px] text-[#525866]">
        {recruiter.quality.toFixed(1)}
      </span>
    </div>
    <div className="px-3.5 py-[10.5px] w-[246.39px] flex items-center">
      <div className="flex items-center gap-[3.5px]">
        <div className="w-[7px] h-[7px] bg-[#f27b2c] rounded-full"></div>
        <span className="font-normal text-[14px] text-[#868c98]">{recruiter.activePositions}</span>
      </div>
    </div>
  </div>
));

/**
 * Complete Statistics Page Component
 * Полная страница статистики с заполненной таблицей кандидатов
 */
export default function StatisticsPage({ 
  onTabChange,
  onUserProfileClick
}: StatisticsPageCompleteProps) {
  const colors = useBrandingColors();
  const rowsPerPage = 7; // Фиксированное количество строк на странице
  const [currentPage, setCurrentPage] = useState(1);
  
  // Состояние для данных из API
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [overviewStats, setOverviewStats] = useState<OverviewStats | null>(null);
  const [recruiterStats, setRecruiterStats] = useState<RecruiterDetailItem[]>([]);
  const [candidatesData, setCandidatesData] = useState<CandidateData[]>([]);
  const [activityData, setActivityData] = useState<any[]>([]);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState<'all' | 'ai' | 'live'>('all');
  const [viewMode, setViewMode] = useState<StatisticsViewMode>('table');

  // Состояние фильтров
  const [searchQuery, setSearchQuery] = useState('');
  // Текущая вакансия берётся из глобального стора
  const selectedVacancyId = useVacancyStore(s => s.selectedVacancyId);
  const selectedVacancy = useVacancyStore(s => s.selectedVacancy);
  const [selectedStatus, setSelectedStatus] = useState<StatisticsInterviewStatusEnum | 'all'>('all');
  const [selectedPeriod, setSelectedPeriod] = useState<StatisticsPeriod>(StatisticsPeriodEnum.ALL);
  const [chartPeriod, setChartPeriod] = useState<StatisticsPeriod>(StatisticsPeriodEnum.WEEK);

  const [selectedCandidate, setSelectedCandidate] = useState<CandidateData | null>(null);
  const [interviewDetails, setInterviewDetails] = useState<any | null>(null);
  const [interviewLoading, setInterviewLoading] = useState(false);
  const [interviewError, setInterviewError] = useState<string | null>(null);
  const [expandedAnswers, setExpandedAnswers] = useState<{ [key: number]: boolean }>({});

  const location = useLocation();

  useEffect(() => {
    setViewMode('table');
    setSelectedCandidate(null);
    setInterviewDetails(null);
    setInterviewError(null);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const handler = () => {
      setViewMode('table');
      setSelectedCandidate(null);
      setInterviewDetails(null);
      setInterviewError(null);
    };
    window.addEventListener('analyticsTabClick', handler);
    return () => window.removeEventListener('analyticsTabClick', handler);
  }, []);


  // Refs для хранения текущих значений фильтров
  const currentFiltersRef = React.useRef({
    selectedPeriod,
    selectedVacancy: selectedVacancyId ?? 'all',
    selectedStatus,
    searchQuery
  });
  
  // Обновляем ref при изменении фильтров
  React.useEffect(() => {
    currentFiltersRef.current = {
      selectedPeriod,
      selectedVacancy: selectedVacancyId ?? 'all',
      selectedStatus,
      searchQuery
    };
  }, [selectedPeriod, selectedVacancyId, selectedStatus, searchQuery]);

  const loadOverviewStats = useCallback(async () => {
    try {
      const data = await statisticsService.getOverviewStats(selectedPeriod, selectedVacancyId ?? undefined);
      setOverviewStats(data);
    } catch (err) {
      console.error('Ошибка загрузки общей статистики:', err);
      setError('Ошибка загрузки общей статистики');
    }
  }, [selectedPeriod, selectedVacancyId]);

  const loadRecruiterStats = useCallback(async () => {
    try {
      const data = await statisticsService.getRecruiterStats(selectedPeriod, selectedVacancyId ?? undefined);
      setRecruiterStats(data);
    } catch (err) {
      console.error('Ошибка загрузки статистики рекрутеров:', err);
    }
  }, [selectedPeriod, selectedVacancyId]);

  const loadCandidatesDataWithParams = useCallback(async (period: StatisticsPeriod, status?: StatisticsInterviewStatusEnum, search?: string, positionId?: number) => {
    try {
      const filters: StatisticsFilters = {
        period,
        status,
        search,
        positionId: positionId ?? (selectedVacancyId ?? undefined),
        page: 0,
        size: 100
      };
      const data = await statisticsService.getRecruitmentResults(filters);
      setCandidatesData(data as CandidateData[]);
    } catch (err) {
      console.error('Ошибка загрузки данных кандидатов:', err);
      setCandidatesData([]);
    }
  }, [selectedVacancyId]);

  // Отдельная функция для загрузки только таблицы с индикатором загрузки
  const loadCandidatesDataWithLoading = useCallback(async () => {
    setIsTableLoading(true);
    try {
      const { selectedStatus: currentStatus, searchQuery: currentSearch } = currentFiltersRef.current;
      const filters: StatisticsFilters = {
        period: selectedPeriod,
        status: currentStatus === 'all' ? undefined : currentStatus,
        search: currentSearch || undefined,
        positionId: selectedVacancyId ?? undefined,
        page: 0,
        size: 100
      };
      const data = await statisticsService.getRecruitmentResults(filters);
      setCandidatesData(data as CandidateData[]);
    } catch (err) {
      console.error('Ошибка загрузки данных кандидатов:', err);
      setCandidatesData([]);
    } finally {
      setIsTableLoading(false);
    }
  }, [selectedPeriod, selectedVacancyId]);

  // Загрузка всех данных (кроме активности - она загружается отдельно)
  const loadAllData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { selectedStatus: currentStatus, selectedVacancy: currentVacancy, searchQuery: currentSearch } = currentFiltersRef.current;
      await Promise.all([
        loadOverviewStats(),
        loadRecruiterStats(),
        loadCandidatesDataWithParams(selectedPeriod, currentStatus === 'all' ? undefined : currentStatus, currentSearch || undefined, currentVacancy && typeof currentVacancy === 'number' ? currentVacancy : undefined)
      ]);
    } catch (err) {
      console.error('Ошибка загрузки данных:', err);
      setError('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  }, [selectedPeriod, loadOverviewStats, loadRecruiterStats, loadCandidatesDataWithParams]);

  // Загрузка данных при монтировании и изменении периода
  useEffect(() => {
    console.log('🔄 StatisticsPage: useEffect сработал - изменился период:', selectedPeriod);
    setLoading(true);
    setError(null);
    const { selectedStatus: currentStatus, searchQuery: currentSearch } = currentFiltersRef.current;
    Promise.all([
      loadOverviewStats(),
      loadRecruiterStats(),
      loadCandidatesDataWithParams(selectedPeriod, currentStatus === 'all' ? undefined : currentStatus, currentSearch || undefined)
    ]).catch(err => {
      console.error('Ошибка загрузки данных:', err);
      setError('Ошибка загрузки данных');
    }).finally(() => {
      setLoading(false);
    });
  }, [selectedPeriod, loadOverviewStats, loadRecruiterStats, loadCandidatesDataWithParams]);

  // Загрузка только таблицы при изменении статуса
  useEffect(() => {
    console.log('🔄 StatisticsPage: useEffect сработал - изменился статус:', selectedStatus);
    loadCandidatesDataWithLoading();
  }, [selectedStatus, loadCandidatesDataWithLoading]);

  // Загрузка только таблицы при изменении вакансии
  useEffect(() => {
    console.log('🔄 StatisticsPage: useEffect сработал - изменилась вакансия (store):', selectedVacancyId);
    loadCandidatesDataWithLoading();
  }, [selectedVacancyId, loadCandidatesDataWithLoading]);

  // Изменение поиска обновляет только таблицу
  useEffect(() => {
    // не дергаем overview/recruiterStats, только таблицу
    if (searchQuery !== '') {
      loadCandidatesDataWithLoading();
    }
  }, [searchQuery, loadCandidatesDataWithLoading]);

  // Сброс страницы на первую при изменении фильтра типа
  useEffect(() => {
    setCurrentPage(1);
  }, [typeFilter]);

  // Загрузка данных активности при изменении периода графика
  useEffect(() => {
    console.log('🔄 StatisticsPage: useEffect сработал - изменился chartPeriod:', chartPeriod);
    const loadData = async () => {
      try {
        // Для графика активности используем только WEEK или MONTH
        const activityPeriod = chartPeriod === StatisticsPeriodEnum.ALL ? StatisticsPeriodEnum.WEEK : chartPeriod;
        console.log('🔗 StatisticsPage: Загружаем данные активности для периода:', activityPeriod);
        const data = await statisticsService.getActivityDataForChart(activityPeriod, selectedVacancyId ?? undefined);
        console.log('🔗 StatisticsPage: Получены данные активности:', data.length, 'записей');
        setActivityData(data);
      } catch (err) {
        console.error('Ошибка загрузки данных активности:', err);
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartPeriod, selectedVacancyId]);

  // Мемоизированные данные для оптимизации производительности
  const memoizedStatusDistribution = useMemo(() => {
    if (!overviewStats?.statusDistribution) return null;
    
    const { successful = 0, inProgress = 0, failed = 0 } = overviewStats.statusDistribution;
    const total = successful + inProgress + failed;
    
    return {
      successful,
      inProgress,
      failed,
      total,
      successfulPercent: total > 0 ? Math.round((successful / total) * 100) : 0,
      inProgressPercent: total > 0 ? Math.round((inProgress / total) * 100) : 0,
      failedPercent: total > 0 ? Math.round((failed / total) * 100) : 0
    };
  }, [overviewStats]);

  const memoizedChartData = useMemo(() => {
    return activityData.map(item => ({
      date: item.date,
      candidates: item.candidates,
      interviews: item.interviews,
      applications: item.applications
    }));
  }, [activityData]);

  const filteredCandidatesForType = useMemo(() => {
    if (!candidatesData?.length) return candidatesData;
    if (typeFilter === 'all') return candidatesData;
    return candidatesData.filter((c: any) => {
      const isLive = c?.interviewType === 'live';
      return typeFilter === 'live' ? isLive : !isLive;
    });
  }, [candidatesData, typeFilter]);

  const hasFilteredCandidates = filteredCandidatesForType.length > 0;

  const handleLiveMeetingStatusChange = useCallback(async (interviewId: number, newStatus: 'scheduled' | 'successful') => {
    // Оптимистично обновляем локальные данные таблицы
    setCandidatesData(prev =>
      prev.map(item =>
        item.id === interviewId ? ({ ...item, status: newStatus as any }) : item
      )
    );
    try {
      await apiService.updateInterviewUiStatus(interviewId, newStatus);
    } catch (e) {
      // В случае ошибки откатываем локально (перезагружаем таблицу)
      console.error('Ошибка сохранения статуса интервью:', e);
      loadCandidatesDataWithLoading();
    }
  }, []);


  // Мемоизированная функция для статус-бейджей
  const getStatusBadge = useCallback((status: string) => {
    const statusConfig = {
      scheduled: {
        label: 'Назначено',
        icon: '●',
        bgColor: 'bg-white',
        textColor: 'text-[#525866]',
        iconColor: 'text-[#8b5cf6]',
        border: 'border-[#e2e4e9]'
      },
      cancelled: {
        label: 'Отменено',
        icon: '–',
        bgColor: 'bg-white',
        textColor: 'text-[#DF1C41]',
        iconColor: 'text-[#DF1C41]',
        border: 'border-[#e2e4e9]'
      },
      [StatusEnum.SUCCESSFUL]: {
        label: 'Успешный',
        icon: '✓',
        bgColor: 'bg-white',
        textColor: 'text-[#525866]',
        iconColor: 'text-[#38C793]',
        border: 'border-[#e2e4e9]'
      },
      [StatusEnum.IN_PROGRESS]: {
        label: 'В процессе',
        icon: '○',
        bgColor: 'bg-white',
        textColor: 'text-[#525866]',
        iconColor: 'text-[#F27B2C]',
        border: 'border-[#e2e4e9]'
      },
      [StatusEnum.FAILED]: {
        label: 'Провал',
        icon: 'X',
        bgColor: 'bg-white',
        textColor: 'text-[#525866]',
        iconColor: 'text-[#3b82f6]',
        border: 'border-[#e2e4e9]'
      }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig[StatusEnum.SUCCESSFUL];

    return (
      <div className={`${config.bgColor} ${config.border} border border-solid rounded-lg flex items-center gap-[3.5px] px-[3.5px] py-[3.5px] pr-[7px] w-[100px]`}>
        <div className={`${config.iconColor} text-[14px] w-[14px] h-[14px] flex items-center justify-center flex-none`}>
          {config.icon}
        </div>
        <span className={`${config.textColor} font-medium text-[12px] leading-[16px] flex-1 text-center`}>
          {config.label}
        </span>
      </div>
    );
  }, []);

  // Бейдж для статуса именно живой встречи (чтобы не дублировать "Успешный")
  const getLiveStatusBadge = useCallback((status: 'scheduled' | 'successful') => {
    const statusConfig = {
      scheduled: {
        label: 'Назначено',
        icon: '●',
        bgColor: 'bg-white',
        textColor: 'text-[#525866]',
        iconColor: 'text-[#8b5cf6]',
        border: 'border-[#e2e4e9]'
      },
      successful: {
        label: 'Пройдено',
        icon: '✓',
        bgColor: 'bg-white',
        textColor: 'text-[#525866]',
        iconColor: 'text-[#38C793]',
        border: 'border-[#e2e4e9]'
      }
    } as const;
    const config = statusConfig[status];
    return (
      <div className={`${config.bgColor} ${config.border} border border-solid rounded-lg flex items-center gap-[3.5px] px-[3.5px] py-[3.5px] pr-[7px] w-[110px]`}>
        <div className={`${config.iconColor} text-[14px] w-[14px] h-[14px] flex items-center justify-center flex-none`}>
          {config.icon}
        </div>
        <span className={`${config.textColor} font-medium text-[12px] leading-[16px] flex-1 text-center`}>
          {config.label}
        </span>
        <span className="text-[#868c98] text-xs flex-none ml-[2px]">▾</span>
      </div>
    );
  }, []);

  // Итоговый бейдж решения для live (Одобрено / Отменено)
  const getLiveDecisionBadge = useCallback((status: 'approved' | 'declined') => {
    const statusConfig = {
      approved: {
        label: 'Одобрено',
        icon: '✓',
        bgColor: 'bg-white',
        textColor: 'text-[#525866]',
        iconColor: 'text-[#38C793]',
        border: 'border-[#e2e4e9]'
      },
      declined: {
        label: 'Отменено',
        icon: '–',
        bgColor: 'bg-white',
        textColor: 'text-[#DF1C41]',
        iconColor: 'text-[#DF1C41]',
        border: 'border-[#e2e4e9]'
      }
    } as const;
    const config = statusConfig[status];
    return (
      <div className={`${config.bgColor} ${config.border} border border-solid rounded-lg flex items-center gap-[3.5px] px-[3.5px] py-[3.5px] pr-[7px] w-[100px]`}>
        <div className={`${config.iconColor} text-[14px] w-[14px] h-[14px] flex items-center justify-center flex-none`}>
          {config.icon}
        </div>
        <span className={`${config.textColor} font-medium text-[12px] leading-[16px] flex-1 text-center`}>
          {config.label}
        </span>
      </div>
    );
  }, []);

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit'
      });
    } catch {
      return '—';
    }
  };

  const toggleAnswer = (questionNumber: number) => {
    setExpandedAnswers(prev => ({
      ...prev,
      [questionNumber]: !prev[questionNumber]
    }));
  };

  const handleCandidateClick = async (candidate: CandidateData) => {
    setSelectedCandidate(candidate);
    setViewMode('candidate-answers');
    if (!candidate?.id) return;
    setInterviewError(null);
    setInterviewLoading(true);
    try {
      const details = await apiService.getInterviewAnswers(candidate.id);
      let merged: any = details;
      try {
        const interviewMeta = await apiService.getInterview(candidate.id);
        merged = {
          ...details,
          interview: {
            ...(details?.interview || {}),
            createdAt: details?.interview?.createdAt || (interviewMeta as any)?.createdAt,
            finishedAt: details?.interview?.finishedAt || (interviewMeta as any)?.finishedAt
          },
          candidate: {
            ...(details?.candidate || {}),
            firstName: details?.candidate?.firstName || (interviewMeta as any)?.candidateFirstName || (candidate as any)?.firstName,
            lastName: details?.candidate?.lastName || (interviewMeta as any)?.candidateLastName || (candidate as any)?.lastName,
            email:
              details?.candidate?.email ||
              (interviewMeta as any)?.candidateEmail ||
              (interviewMeta as any)?.email ||
              (candidate as any)?.candidateEmail ||
              (candidate as any)?.email
          }
        };
      } catch (metaErr) {
        console.warn('Не удалось загрузить метаданные интервью:', metaErr);
      }
      setInterviewDetails(merged);
    } catch (err) {
      console.error('Ошибка загрузки данных интервью:', err);
      setInterviewError('Ошибка загрузки данных интервью');
    } finally {
      setInterviewLoading(false);
    }
  };

  const handleDownloadPdf = useCallback(async () => {
    if (isPdfLoading) return;
    setIsPdfLoading(true);
    try {
      const { selectedPeriod: currentPeriod, searchQuery: currentSearch } = currentFiltersRef.current;
      const filtersForReport = {
        positionId: selectedVacancyId ?? undefined,
        period: currentPeriod,
        search: currentSearch || undefined,
        maxRows: 1000,
      };
      const filename = `recruitment-results-${new Date().toISOString().split('T')[0]}.pdf`;
      await apiService.downloadRecruitmentResultsPDF(filtersForReport, filename);
    } catch (err) {
      console.error('Ошибка скачивания PDF отчета:', err);
    } finally {
      setIsPdfLoading(false);
    }
  }, [isPdfLoading, selectedVacancyId]);

  // Обработка состояний загрузки и ошибок
  if (loading) {
    return (
      <div className="bg-[#e9eae2] relative min-h-screen w-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3b82f6] mx-auto mb-4"></div>
          <p className="text-[#525866]">Загрузка статистики...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-[#e9eae2] relative min-h-screen w-full flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-lg mb-4">⚠️</div>
          <p className="text-[#525866] mb-4">{error}</p>
          <button 
            onClick={() => loadAllData()}
            className="text-white px-4 py-2 rounded-lg transition-colors"
            style={getButtonInlineStyles(colors, 'primary')}
            onMouseEnter={(e) => {
              Object.assign(e.currentTarget.style, getButtonHoverStyles(colors, 'primary'));
            }}
            onMouseLeave={(e) => {
              Object.assign(e.currentTarget.style, getButtonInlineStyles(colors, 'primary'));
            }}
          >
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen w-full">
      <div className="relative w-full">
        <div className="flex flex-col gap-6 w-full h-full">
          {/* Main Content - единый контейнер как в Figma */}
          <div className="bg-[#f5f6f1] box-border content-stretch flex flex-col gap-[10px] items-start justify-start relative shrink-0 w-full flex-1 h-full overflow-hidden">
            <div className={`w-full h-full overflow-y-auto overscroll-y-contain flex flex-col ${viewMode === 'table' ? 'px-6 py-8' : 'px-6'}`}>
              
              {/* Заголовок блока как на странице вакансии */}
              {viewMode === 'table' && (
                <h1 className="text-2xl ml-2 font-semibold text-[#0a0d14] mb-6">Итоги Подбора</h1>
              )}

              {/* Candidates Table / Candidate Answers */}
              {viewMode === 'table' ? (
                <div className="bg-[#f5f6f1] rounded-[24px] p-1 w-full">
                  <div className="flex flex-col gap-[21px]">
                    {/* Таблица */}
                    <div className="bg-white rounded-[16px] p-6">
                      <div className="flex flex-col gap-[8.75px]">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="bg-[#f6f8fa] p-1 rounded-xl flex items-center gap-1">
                            <button
                              onClick={() => setTypeFilter('all')}
                              className={`px-3 py-1 rounded-lg text-[12px] font-medium transition-colors ${typeFilter === 'all' ? 'bg-white text-[#0a0d14] shadow-sm' : 'text-[#868c98]'}`}
                            >
                              Все
                            </button>
                            <button
                              onClick={() => setTypeFilter('ai')}
                              className={`px-3 py-1 rounded-lg text-[12px] font-medium transition-colors ${typeFilter === 'ai' ? 'bg-white text-[#0a0d14] shadow-sm' : 'text-[#868c98]'}`}
                            >
                              AI
                            </button>
                            <button
                              onClick={() => setTypeFilter('live')}
                              className={`px-3 py-1 rounded-lg text-[12px] font-medium transition-colors ${typeFilter === 'live' ? 'bg-white text-[#0a0d14] shadow-sm' : 'text-[#868c98]'}`}
                            >
                              Live
                            </button>
                          </div>
                        </div>
                        <DownloadPdfButton
                          size="medium"
                          label="Скачать отчет PDF"
                          onClick={handleDownloadPdf}
                          disabled={isPdfLoading}
                        />
                      </div>
                      {/* Индикатор загрузки таблицы */}
                      {isTableLoading && (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3b82f6]"></div>
                          <span className="ml-3 text-[#525866]">Загрузка данных...</span>
                        </div>
                      )}
                      
                      {/* Пустое состояние таблицы */}
                      {!isTableLoading && !hasFilteredCandidates && (
                        <div className="flex flex-col items-center justify-center py-12">
                          <div className="w-16 h-16 bg-[#f6f8fa] rounded-full flex items-center justify-center mb-4">
                            <SearchIcon className="w-8 h-8 text-[#868C98]" />
                          </div>
                          <h3 className="text-lg font-medium text-[#0a0d14] mb-2">Нет данных</h3>
                          <p className="text-[#525866] text-center max-w-sm">
                            По выбранным фильтрам не найдено ни одного собеседования. Попробуйте изменить параметры поиска.
                          </p>
                        </div>
                      )}

                      {/* Заголовки таблицы - показываем только если есть данные */}
                      {!isTableLoading && hasFilteredCandidates && (() => {
                        const fioWidth = typeFilter === 'live' ? '1 1 330px' : '1 1 240px';
                        return (
                          <div className="bg-[#f6f8fa] rounded-[10px] flex overflow-hidden w-full">
                            {/* ФИО */}
                            <div className="px-3.5 py-[10.5px] overflow-hidden" style={{ flex: fioWidth, minWidth: '200px' }}>
                              <span className="font-medium text-[14px] text-[#525866]">ФИО</span>
                            </div>
                            
                            {/* Контакты */}
                            <div className="px-3.5 py-[10.5px] overflow-hidden" style={{ flex: '0 0 150px', minWidth: '120px' }}>
                              <span className="font-medium text-[14px] text-[#525866]">Контакты</span>
                            </div>
                            
                            {/* Тип - только для "all" */}
                            {typeFilter === 'all' && (
                              <div className="px-3.5 py-[10.5px] overflow-hidden" style={{ flex: '0 0 100px', minWidth: '80px' }}>
                                <span className="font-medium text-[14px] text-[#525866]">Тип</span>
                              </div>
                            )}
                            
                            {/* Оценка - для "all" и "ai" */}
                            {(typeFilter === 'all' || typeFilter === 'ai') && (
                              <div className="px-3.5 py-[10.5px] overflow-hidden text-center" style={{ flex: '0 0 90px', minWidth: '70px' }}>
                                <span className="font-medium text-[14px] text-[#525866]">Оценка</span>
                              </div>
                            )}
                            
                            {/* Дата */}
                            <div className="px-3.5 py-[10.5px] overflow-hidden text-right" style={{ flex: '0 0 110px', minWidth: '90px' }}>
                              <span className="font-medium text-[14px] text-[#525866]">Дата</span>
                            </div>
                            
                            {/* Статус */}
                            <div className="px-3.5 py-[10.5px] overflow-hidden" style={{ flex: '0 0 130px', minWidth: '110px' }}>
                              <span className="font-medium text-[14px] text-[#525866]">Статус</span>
                            </div>
                            
                            {/* Статус встречи - только для "all" и "live" */}
                            {typeFilter !== 'ai' && (
                              <div className="px-3.5 py-[10.5px] overflow-hidden" style={{ flex: typeFilter === 'live' ? '0 0 180px' : '1 1 150px', minWidth: '130px' }}>
                                <span className="font-medium text-[14px] text-[#525866]">Статус встречи</span>
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* Строки данных */}
                      {!isTableLoading && hasFilteredCandidates && (
                        <div className="flex flex-col gap-[1.8px] w-full">
                          {(() => {
                            const total = filteredCandidatesForType.length;
                            const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));
                            const safeCurrentPage = Math.min(currentPage, totalPages);
                            const start = (safeCurrentPage - 1) * rowsPerPage;
                            const end = start + rowsPerPage;
                            const pageItems = filteredCandidatesForType.slice(start, end);
                            if (safeCurrentPage !== currentPage) {
                              // keep state in sync if data size changed
                              setCurrentPage(safeCurrentPage);
                            }
                            return pageItems.map((candidate, index) => (
                            <CandidateRow
                              key={candidate.id}
                              candidate={candidate}
                                index={index}
                                totalLength={pageItems.length}
                              getStatusBadge={getStatusBadge}
                              getLiveStatusBadge={getLiveStatusBadge}
                              getLiveDecisionBadge={getLiveDecisionBadge}
                              typeFilter={typeFilter}
                              onLiveMeetingStatusChange={handleLiveMeetingStatusChange}
                              onCandidateClick={handleCandidateClick}
                            />
                            ));
                          })()}
                        </div>
                      )}
                      </div>
                    </div>
                    
                    {/* Пагинация */}
                    {!isTableLoading && hasFilteredCandidates && (
                      <div className="flex justify-center w-full shrink-0">
                        <Pagination
                          currentPage={currentPage}
                          totalPages={Math.max(1, Math.ceil(filteredCandidatesForType.length / rowsPerPage))}
                          onPageChange={setCurrentPage}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                selectedCandidate && (
                  <div className="bg-[#f5f6f1] rounded-[24px] p-4 w-full">
                    <CandidateAnswersPanel
                      selectedCandidate={selectedCandidate}
                      selectedPositionId={selectedVacancyId ?? null}
                      selectedPositionTitle={selectedVacancy?.title}
                      selectedPosition={selectedVacancy}
                      interviewLoading={interviewLoading}
                      interviewError={interviewError}
                      interviewDetails={interviewDetails}
                      expandedAnswers={expandedAnswers}
                      formatDate={formatDate}
                      onRetry={() => handleCandidateClick(selectedCandidate)}
                      onToggleAnswer={toggleAnswer}
                      onBack={() => setViewMode('table')}
                    />
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
